<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      :settings="{
        theme: 'ag-theme-alpine',
        customRowsKey: 'items',
        title: t('reportTitle')
      } as any"
      endpoint="reports/accounting/profit-by-item"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3"
          :placeholder="t('selectItem')"
          v-model="selectedItemID"
          @change="onFilterChanged"
          value-key="id"
          remote
          :remote-method="searchItems"
        >
          <el-option
            v-for="option in items"
            :key="option.id"
            :label="option.name"
            :value="option.id"
          ></el-option>
        </el-select>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3"
          :placeholder="t('selectCategory')"
          v-model="selectedCategoryID"
          @change="onFilterChanged"
        >
          <el-option
            v-for="option in categories"
            :key="option.id"
            :label="option.name"
            :value="option.id"
          ></el-option>
        </el-select>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3"
          :placeholder="t('selectBrand')"
          v-model="selectedBrandID"
          @change="onFilterChanged"
        >
          <el-option
            v-for="option in brands"
            :key="option.id"
            :label="option.name"
            :value="option.id"
          ></el-option>
        </el-select>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3"
          :placeholder="t('selectTag')"
          v-model="selectedTagID"
          @change="onFilterChanged"
        >
          <el-option
            v-for="option in tags"
            :key="option.id"
            :label="option.name"
            :value="option.id"
          ></el-option>
        </el-select>
      </template>
      <template #cards>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-sm text-gray-500">{{ t('totalQtySold') }}</div>
              <div class="text-xl font-bold pt-2">{{ formatNumber(insight.totalQtySold) }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-sm text-gray-500">{{ t('totalRevenue') }}</div>
              <div class="text-xl font-bold pt-2 text-blue-600">{{ formatMoney(insight.totalRevenue, insight.currency, insight.precision) }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-sm text-gray-500">{{ t('totalCogs') }}</div>
              <div class="text-xl font-bold pt-2 text-red-600">{{ formatMoney(insight.totalCogs, insight.currency, insight.precision) }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-sm text-gray-500">{{ t('totalGrossProfit') }}</div>
              <div class="text-xl font-bold pt-2" :class="insight.totalGrossProfit >= 0 ? 'text-green-600' : 'text-red-600'">
                {{ formatMoney(insight.totalGrossProfit, insight.currency, insight.precision) }}
              </div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-sm text-gray-500">{{ t('marginTotal') }}</div>
              <div class="text-xl font-bold pt-2" :class="insight.marginTotal >= 0 ? 'text-green-600' : 'text-red-600'">
                {{ formatPercent(insight.marginTotal) }}
              </div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-sm text-gray-500">{{ t('markupTotal') }}</div>
              <div class="text-xl font-bold pt-2" :class="insight.markupTotal >= 0 ? 'text-green-600' : 'text-red-600'">
                {{ formatPercent(insight.markupTotal) }}
              </div>
            </div>
          </el-card>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useHTTP } from '@/plugins/http'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import Item from '@/models/inventory/Item'
import Category from '@/models/inventory/Category'
import Brand from '@/models/inventory/Brand'
import Tag from '@/models/inventory/Tag'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      reportTitle: 'Profit by Item',
      selectItem: 'Select Item',
      selectCategory: 'Select Category',
      selectBrand: 'Select Brand',
      selectTag: 'Select Tag',
      itemName: 'Item Name',
      qtySold: 'Qty Sold',
      revenue: 'Revenue',
      cogs: 'COGS',
      grossProfit: 'Gross Profit',
      margin: 'Margin',
      markup: 'Markup',
      totalQtySold: 'Total Qty Sold',
      totalRevenue: 'Total Revenue',
      totalCogs: 'Total COGS',
      totalGrossProfit: 'Total Gross Profit',
      marginTotal: 'Margin Total',
      markupTotal: 'Markup Total',
    },
    ar: {
      reportTitle: 'الأرباح بحسب المنتج',
      selectItem: 'اختر المنتج',
      selectCategory: 'اختر الصنف',
      selectBrand: 'اختر العلامة التجارية',
      selectTag: 'اختر الوسم',
      itemName: 'اسم المنتج',
      qtySold: 'الكمية المباعة',
      revenue: 'العائد',
      cogs: 'تكلفة المبيعات',
      grossProfit: 'إجمالي الارباح',
      margin: 'هامش الارباح',
      markup: 'هامش الربح على التكلفة',
      totalQtySold: 'إجمالي الكمية المباعة',
      totalRevenue: 'العائد الكلي',
      totalCogs: 'تكلفة المبيعات الكلية',
      totalGrossProfit: 'إجمالي اللأرباح الكلية',
      marginTotal: 'هامش الأرباح الكلية',
      markupTotal: 'هامش الربح على التكلفة',
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])
const http = useHTTP()

// Filters
const items = ref<Item[]>([])
const categories = ref<Category[]>([])
const brands = ref<Brand[]>([])
const tags = ref<Tag[]>([])

const selectedItemID = ref<string>()
const selectedCategoryID = ref<string>()
const selectedBrandID = ref<string>()
const selectedTagID = ref<string>()

// Insight data
const insight = ref({
  totalQtySold: 0,
  totalRevenue: 0,
  totalCogs: 0,
  totalGrossProfit: 0,
  marginTotal: 0,
  markupTotal: 0,
  currency: 'IQD',
  precision: 0
})

onBeforeMount(async () => {
  // Set column definitions first (synchronously)
  columnDefs.value = [
    {
      headerName: t('itemName'),
      field: 'itemName',
      minWidth: 250,
    },
    {
      headerName: t('qtySold'),
      field: 'qtySold',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatNumber(data.qtySold)
      }
    },
    {
      headerName: t('revenue'),
      field: 'revenue',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.revenue, data.currency, data.precision)
      },
      cellStyle: { color: '#2563eb' }
    },
    {
      headerName: t('cogs'),
      field: 'cogs',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.cogs, data.currency, data.precision)
      },
      cellStyle: { color: '#dc2626' }
    },
    {
      headerName: t('grossProfit'),
      field: 'grossProfit',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.grossProfit, data.currency, data.precision)
      },
      cellStyle: ({ data }) => {
        if (!data) return {}
        return { color: data.grossProfit >= 0 ? '#16a34a' : '#dc2626', fontWeight: 'bold' }
      }
    },
    {
      headerName: t('margin'),
      field: 'margin',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatPercent(data.margin)
      },
      cellStyle: ({ data }) => {
        if (!data) return {}
        return { color: data.margin >= 0 ? '#16a34a' : '#dc2626' }
      }
    },
    {
      headerName: t('markup'),
      field: 'markup',
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatPercent(data.markup)
      },
      cellStyle: ({ data }) => {
        if (!data) return {}
        return { color: data.markup >= 0 ? '#16a34a' : '#dc2626' }
      }
    },
  ]

  // Load filter options (async, after columns are set)
  Promise.all([
    loadCategories(),
    loadBrands(),
    loadTags()
  ])
})

async function loadCategories() {
  try {
    const resp = await http.get<Category[]>(Category.ENDPOINT)
    categories.value = resp.map(c => Category.from(c))
  } catch (error) {
    console.error('Error loading categories:', error)
  }
}

async function loadBrands() {
  try {
    const resp = await http.get<Brand[]>(Brand.ENDPOINT)
    brands.value = resp.map(b => Brand.from(b))
  } catch (error) {
    console.error('Error loading brands:', error)
  }
}

async function loadTags() {
  try {
    const resp = await http.get<Tag[]>(Tag.ENDPOINT)
    tags.value = resp.map(t => Tag.from(t))
  } catch (error) {
    console.error('Error loading tags:', error)
  }
}

async function searchItems(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Item.ENDPOINT}?q=${query}`
    const itemsRes = await http.get<Item[]>(url)
    items.value = itemsRes.map((item) => Item.from(item))
  } catch (error) {
    console.error('Error searching items:', error)
  }
}

function onDataLoaded(data: any) {
  if (!data) return

  // Update insight from response
  if (data.insight) {
    insight.value = {
      totalQtySold: data.insight.totalQtySold || 0,
      totalRevenue: data.insight.totalRevenue || 0,
      totalCogs: data.insight.totalCogs || 0,
      totalGrossProfit: data.insight.totalGrossProfit || 0,
      marginTotal: data.insight.marginTotal || 0,
      markupTotal: data.insight.markupTotal || 0,
      currency: data.insight.currency || 'IQD',
      precision: data.insight.precision || 0
    }
  } else {
    // Reset insight if no data
    insight.value = {
      totalQtySold: 0,
      totalRevenue: 0,
      totalCogs: 0,
      totalGrossProfit: 0,
      marginTotal: 0,
      markupTotal: 0,
      currency: 'IQD',
      precision: 0
    }
  }
}

function onFilterChanged() {
  if (rtable.value) {
    const payload = rtable.value.payload
    payload.item_id = selectedItemID.value || null
    payload.category_id = selectedCategoryID.value || null
    payload.brand_id = selectedBrandID.value || null
    payload.tag_id = selectedTagID.value || null
    rtable.value.navigate(payload)
  }
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0'
  return value.toLocaleString()
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0%'
  return `${value.toFixed(2)}%`
}
</script>
